'use client';

/**
 * Chart Component
 *
 * Base chart wrapper providing theme integration and consistent styling.
 * Use this as a wrapper for all Recharts charts to ensure theme-aware colors.
 *
 * @example
 * ```tsx
 * <Chart height={300}>
 *   <LineChart data={data}>
 *     <Line dataKey="value" />
 *   </LineChart>
 * </Chart>
 * ```
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import React from 'react';
import { ResponsiveContainer } from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';
import { type NeonAccent, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface ChartProps extends Omit<BoxProps, 'children'> {
  /**
   * Chart height in pixels
   * @default 300
   */
  height?: number;

  /**
   * Chart width (responsive by default)
   * @default '100%'
   */
  width?: number | `${number}%`;

  /**
   * Minimum width for responsive container
   * @default 0
   */
  minWidth?: number;

  /**
   * Debounce resize events (ms)
   * @default 0
   */
  debounce?: number;

  /**
   * Optional neon accent for glow styling
   */
  neonAccent?: NeonAccent;

  /**
   * Enable glassmorphism background
   * @default false
   */
  glassBackground?: boolean;

  /**
   * Custom padding
   * @default 0
   */
  padding?: number | string;

  /**
   * Children (Recharts chart components)
   */
  children: React.ReactElement;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledChartContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $glassBackground: boolean;
  $padding: number | string;
}

const StyledChartContainer = styled(
  Box,
  transientOptions,
)<StyledChartContainerProps>(({ theme, $isDark, $neonAccent, $glassBackground, $padding }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonColors = $neonAccent ? neonAccents[$neonAccent] : null;

  return {
    position: 'relative',
    width: '100%',
    padding: typeof $padding === 'number' ? theme.spacing($padding) : $padding,

    // Glassmorphism background
    ...($glassBackground && {
      background: neonColors ? ($isDark ? neonColors.bg : neonColors.bgLight) : glass.background,
      borderRadius: 12,
      border: `1px solid ${neonColors ? ($isDark ? neonColors.border : neonColors.borderLight) : glass.border}`,
      backdropFilter: glass.backdropBlur,
    }),

    // Recharts tooltip styling override
    '& .recharts-tooltip-wrapper': {
      zIndex: 10,
    },
  };
});

// ============================================================================
// Chart Context for sharing colors
// ============================================================================

export interface ChartContextValue {
  colors: ReturnType<typeof useChartColors>;
  neonAccent?: NeonAccent;
}

export const ChartContext = React.createContext<ChartContextValue | null>(null);

/**
 * Hook to access chart context colors
 */
export function useChartContext(): ChartContextValue {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChartContext must be used within a Chart component');
  }
  return context;
}

// ============================================================================
// Component
// ============================================================================

export const Chart: React.FC<ChartProps> = ({
  children,
  height = 300,
  width = '100%',
  minWidth = 0,
  debounce = 0,
  neonAccent,
  glassBackground = false,
  padding = 0,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = useChartColors();

  const contextValue: ChartContextValue = {
    colors,
    neonAccent,
  };

  return (
    <ChartContext.Provider value={contextValue}>
      <StyledChartContainer
        $isDark={isDark}
        $neonAccent={neonAccent}
        $glassBackground={glassBackground}
        $padding={padding}
        sx={sx}
        {...props}
      >
        <ResponsiveContainer width={width} height={height} minWidth={minWidth} debounce={debounce}>
          {children}
        </ResponsiveContainer>
      </StyledChartContainer>
    </ChartContext.Provider>
  );
};

Chart.displayName = 'Chart';

export default Chart;
